import React, { useEffect } from 'react';
import { ViewStyle } from 'react-native';
import { useRouter } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import { useGetUser } from '@/shared/hooks/useGetUser';
import StylistProfile from '@/shared/components/StylistProfile';
import useStore from '@/stores/useStore';
import ClientProfile from '@/shared/components/ClientProfile';
import Button from '@/shared/components/Button';
import {
  PageTitle,
  StyledAsterisksText,
  StyledBodyText,
} from '@/shared/styles/styledComponents/editProfileForm';
import styled from '@emotion/native';
import { mintGreen, white, sand } from '@/shared/styles/colors';
import BackToTopButton from '@/shared/components/BackToTopButton';
import { useScrollToTop } from '@/shared/hooks/useScrollToTop';
import ScrollableView from '@/shared/components/ScrollableView';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  MutationResult,
  QueryNextAvailableSlotArgs,
  QueryResult,
} from '@/shared/types/generated';
import { GET_STYLIST_NEXT_AVAILABLE_SLOT } from '@/shared/queries/stylistSchema';
import SentryService from '@/services/sentry/Sentry.service';
import { formatNameWithLastInitial } from '@/shared/utils';
import { GENERATE_SHARE_PROFILE_URL } from '@/shared/mutations';

const ProfilePreview = () => {
  const router = useRouter();
  const scrollToTop = useScrollToTop();

  const { user } = useGetUser();
  const isStylist = useStore((state) => state.isStylist);

  const [getNextAvailableSlot, { data: nextAvailableSlotData }] = useLazyQuery<
    QueryResult<'nextAvailableSlot'>,
    QueryNextAvailableSlotArgs
  >(GET_STYLIST_NEXT_AVAILABLE_SLOT, {
    fetchPolicy: 'network-only',
    onError: (error) => {
      SentryService.captureError(
        `Error fetching next available slot in preview for stylist ${user?.stylist?.id}`,
        error,
      );
    },
  });

  const [generateShareProfileUrl, { data: shareProfileUrlData }] = useMutation<
    MutationResult<'generateShareProfileUrl'>
  >(GENERATE_SHARE_PROFILE_URL, {
    onError: (error) => {
      SentryService.captureError(
        `Error generating share profile url in preview for stylist ${user?.stylist?.id}`,
        error,
      );
    },
  });

  const userName = formatNameWithLastInitial(user).toUpperCase();

  useEffect(() => {
    if (isStylist && user?.stylist?.id) {
      getNextAvailableSlot({
        variables: {
          input: {
            stylistId: user?.stylist?.id,
            currentDate: new Date().toISOString(),
          },
        },
      });

      generateShareProfileUrl();
    }
  }, [
    generateShareProfileUrl,
    getNextAvailableSlot,
    isStylist,
    user?.stylist?.id,
  ]);

  if (isStylist) {
    return (
      <StylistProfileContainer>
        <StylistProfile
          stylistName={userName}
          expertise={user?.stylist?.expertise ?? []}
          aesthetics={user?.stylist?.aesthetics ?? []}
          profilePicUrl={user?.profilePicUrl ?? ''}
          instagramHandle={user?.stylist?.instagram ?? ''}
          tiktokHandle={user?.stylist?.tiktok ?? ''}
          substackHandle={user?.stylist?.substack ?? ''}
          bio={user?.stylist?.bio ?? ''}
          landscapeImage={user?.stylist?.landscapeImage ?? null}
          portraitImages={user?.stylist?.portraitImages ?? []}
          pricing={user?.stylist?.pricing ?? undefined}
          customHourlyRate={user?.stylist?.customHourlyRate ?? undefined}
          customInPersonHourlyRate={
            user?.stylist?.customInPersonHourlyRate ?? undefined
          }
          isAvailableInPerson={user?.stylist?.isAvailableInPerson ?? undefined}
          nextAvailableSlot={
            nextAvailableSlotData?.nextAvailableSlot ?? undefined
          }
          shareProfileUrl={
            shareProfileUrlData?.generateShareProfileUrl?.shareProfileUrl ?? ''
          }
          showSubheaderSection
          outerContainerStyle={{ backgroundColor: white }}
          showPreviewLabel
        />
      </StylistProfileContainer>
    );
  }

  return (
    <ScrollableView contentContainerStyle={clientScrollContainerStyle}>
      <ClientTopContentContainer style={{ marginTop: 36, marginBottom: 24 }}>
        <PageTitle>Your Profile</PageTitle>
        <StyledBodyText style={{ marginBottom: 24 }}>
          Stylists will check out your profile to tailor their
          recommendations—make sure it represents you.
        </StyledBodyText>
        <Button
          onPress={() => router.push(ROUTES.PROFILE.path)}
          text="Edit Profile"
          variant="tertiary"
          size="small"
          customStyle={{
            width: 165,
            height: 48,
          }}
        />
      </ClientTopContentContainer>
      <ClientProfileContainer>
        <StyledAsterisksText>Preview:</StyledAsterisksText>
        <ClientProfile
          clientName={userName}
          profilePicUrl={user?.profilePicUrl ?? ''}
          aesthetics={user?.client?.aesthetics ?? []}
          bio={user?.client?.bio}
          sizingDetails={user?.client?.sizingDetails}
          height={user?.client?.height}
          city={user?.address?.city}
          state={user?.address?.state}
          birthDate={user?.birthDate}
          shareAge={user?.client?.shareAge}
          showShadow
        />
        <Spacer />
        <BackToTopButtonContainer>
          <BackToTopButton scrollToTop={scrollToTop} />
        </BackToTopButtonContainer>
      </ClientProfileContainer>
    </ScrollableView>
  );
};

export default ProfilePreview;

const StylistProfileContainer = styled.View({
  flex: 1,
  backgroundColor: white,
});

const clientScrollContainerStyle: ViewStyle = {
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: sand,
};

const ClientTopContentContainer = styled.View({
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: 32,
  width: '100%',
  backgroundColor: sand,
  maxWidth: 342,
});

const ClientProfileContainer = styled.View({
  flex: 1,
  backgroundColor: mintGreen,
  padding: 28,
});

const Spacer = styled.View({
  height: 60,
});

const BackToTopButtonContainer = styled.View({
  alignItems: 'center',
});
